import { useState } from "react";
import { Link } from "react-router-dom";
import { sendResetCode, resetPasswordWithCode } from "../../services/auth";
import RIcon from "../../components/RIcon";

type Step = "request" | "verify" | "done";

export default function ForgotPasswordPage() {
  const [step, setStep] = useState<Step>("request");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [cooldown, setCooldown] = useState(0);

  const startCooldown = (seconds: number) => {
    setCooldown(seconds);
    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setInfo("");
    if (!username.trim() || !email.trim()) {
      setError("请输入用户名和邮箱");
      return;
    }
    setLoading(true);
    try {
      const res = await sendResetCode(username.trim(), email.trim());
      setInfo(res.message || "验证码已发送");
      setStep("verify");
      startCooldown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : "发送验证码失败");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError("");
    try {
      await sendResetCode(username.trim(), email.trim());
      setInfo("验证码已重新发送");
      startCooldown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : "发送失败");
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!code.trim() || code.trim().length !== 6) {
      setError("请输入 6 位验证码");
      return;
    }
    if (newPassword.length < 8) {
      setError("新密码至少 8 位");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("两次密码不一致");
      return;
    }
    setLoading(true);
    try {
      await resetPasswordWithCode({
        username: username.trim(),
        email: email.trim(),
        code: code.trim(),
        new_password: newPassword,
      });
      setStep("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "重置失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[#F4F8FB] overflow-y-auto p-4">
      <div className="bg-white rounded-2xl p-10 max-w-md w-full shadow-sm border border-gray-100 my-4">
        <div className="flex items-center gap-2 mb-8">
          <img
            src="https://public.readdy.ai/ai/img_res/a84828b1-10f3-4f8c-851b-b2f4fe56a73c.png"
            alt="协和医疗"
            className="h-7 w-auto"
          />
          <span className="text-[#1A2B3C] font-semibold text-sm">协和医疗全域AI系统</span>
        </div>

        <div className="mb-6">
          <h2 className="text-xl font-bold text-[#1A2B3C] mb-1">找回密码</h2>
          <p className="text-[#718096] text-sm">
            通过注册邮箱接收验证码，验证后即可设置新密码（仅启用账号可重置）。
          </p>
        </div>

        {step === "done" ? (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-emerald-700 text-sm leading-relaxed">
              <RIcon name="checkbox-circle-line" className="mr-1" />
              密码已重置成功，请使用新密码登录。
            </div>
            <Link
              to="/login"
              className="block text-center w-full h-11 leading-[44px] bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-semibold"
            >
              前往登录
            </Link>
          </div>
        ) : step === "request" ? (
          <form onSubmit={handleSendCode} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">用户名</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="请输入登录用户名"
                className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">注册邮箱</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="注册时使用的邮箱"
                className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
              />
            </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
            {info && <p className="text-[#0EA5A0] text-xs">{info}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer"
            >
              {loading ? (
                <>
                  <RIcon name="loader-4-line" className="animate-spin" />
                  发送中...
                </>
              ) : (
                "发送验证码"
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleReset} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">邮箱验证码</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="6 位数字"
                  className="flex-1 border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0] tracking-widest"
                />
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={cooldown > 0}
                  className="h-10 px-3 border border-[#0EA5A0] text-[#0EA5A0] rounded-lg text-xs disabled:opacity-50 whitespace-nowrap"
                >
                  {cooldown > 0 ? `${cooldown}s 后重发` : "重新发送"}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">新密码</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="至少 8 位"
                className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A2B3C] mb-1.5">确认新密码</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="再次输入新密码"
                className="w-full border border-gray-200 rounded-lg px-3 h-10 text-sm outline-none focus:border-[#0EA5A0]"
              />
            </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
            {info && <p className="text-[#0EA5A0] text-xs">{info}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer"
            >
              {loading ? (
                <>
                  <RIcon name="loader-4-line" className="animate-spin" />
                  提交中...
                </>
              ) : (
                "重置密码"
              )}
            </button>
          </form>
        )}

        <p className="text-center text-sm text-[#718096] mt-5">
          <Link
            to="/login"
            className="text-[#0EA5A0] font-medium hover:underline flex items-center justify-center gap-1"
          >
            <RIcon name="arrow-left-line" className="text-xs" />
            返回登录
          </Link>
        </p>
      </div>
    </div>
  );
}
